/**
 * Column utilities for TAS Programs reporting.
 *
 * Pure functions for field-key normalization and field-option grouping/sorting.
 */
import type { FieldOption } from './opensearch-field-mapper';

/**
 * Normalize a field key for case-/format-insensitive comparison.
 * Strips dots, underscores and lowercases so that camelCase keys
 * (e.g. `employeeNameKeyword`) match their snake_case equivalents
 * (e.g. `employee_name_keyword`).
 */
export function normalizeFieldKey(key: string): string {
  return key.toLowerCase().replace(/[_.]/g, '');
}

/**
 * Canonical display-order for field groups in the column picker.
 * "Admin Configuration" is deliberately last.
 */
const GROUP_SORT_ORDER = [
  'Employee',
  'Employment',
  'Contact Information',
  'Postal Address',
  'Identifiers',
  'Program Instance',
  'Program Template',
  'Application',
  'Applicant',
  'Limit',
  'Eligibility',
  'Evidence',
  'Digital Assets & Attachments',
  'Review & Queue',
  'Courses',
  'Participant',
  'LNP / Learning Partner',
  'Withdrawal',
  'Payments',
  'Scholarships',
  'Assistance',
  'Benefit Year',
  'Company Settings',
  'PSLF',
  'SYF',
  'SLR',
  'Form',
  'Borrower Fields',
  'Employer Fields',
  'Plan Settings & Assignments',
  'Borrower',
  'Organization',
  'Loans',
  'Match Data',
  'Contribution',
  'Company',
  'Other',
  'Admin Configuration', // Always at the bottom
];

/**
 * Group an array of `FieldOption`s by their `.group` property, then sort the
 * groups according to `GROUP_SORT_ORDER`.  Within a base group, " / Custom"
 * subgroups sort after their parent.
 */
export function groupFieldOptions(fields: FieldOption[]): Array<[string, FieldOption[]]> {
  const groups = new Map<string, FieldOption[]>();

  for (const field of fields) {
    const group = field.group || 'Other';
    let list = groups.get(group);
    if (!list) {
      list = [];
      groups.set(group, list);
    }
    list.push(field);
  }

  const groupedArray = Array.from(groups.entries());

  const customSuffix = ' / Custom';

  const parseGroupSortMeta = (groupName: string) => {
    const isCustomSubgroup = groupName.endsWith(customSuffix);
    const baseGroup = isCustomSubgroup
      ? groupName.slice(0, groupName.length - customSuffix.length)
      : groupName;
    return { baseGroup, isCustomSubgroup };
  };

  const getGroupPosition = (baseGroup: string) => {
    // "Admin Configuration" is always last, regardless of other groups.
    if (baseGroup === 'Admin Configuration') {
      return Number.POSITIVE_INFINITY;
    }

    const index = GROUP_SORT_ORDER.indexOf(baseGroup);

    // Groups not in the order list go after known non-admin groups,
    // but still before "Admin Configuration".
    return index === -1 ? GROUP_SORT_ORDER.length : index;
  };

  return groupedArray.sort((a, b) => {
    const aMeta = parseGroupSortMeta(a[0]);
    const bMeta = parseGroupSortMeta(b[0]);
    const aPosition = getGroupPosition(aMeta.baseGroup);
    const bPosition = getGroupPosition(bMeta.baseGroup);
    if (aPosition !== bPosition) {
      return aPosition - bPosition;
    }

    // Within same base group: base group first, then Custom subgroup
    if (aMeta.isCustomSubgroup !== bMeta.isCustomSubgroup) {
      return aMeta.isCustomSubgroup ? 1 : -1;
    }

    return a[0].localeCompare(b[0]);
  });
}
